import type { ResourceBase } from './interfaces/resourceInterface';
import { type ResourceStatusKey, resourceStatusKeys } from './interfaces/resourceStatusInterface';

// https://stackoverflow.com/a/53486112/2518608
export function debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
	let timeoutId: null | ReturnType<typeof setTimeout> = null;

	return function (this: unknown, ...args: unknown[]) {
		clearTimeout(timeoutId as ReturnType<typeof setTimeout>);

		timeoutId = setTimeout(() => {
			fn.apply(this, args);
		}, delay);
	} as T;
}

// Regular Object.keys only returns string[], so this function returns a typed version
export const getKeys = Object.keys as <T extends object>(obj: T) => Array<keyof T>;

// Regular Object.entries only returns [string, string], so this function returns a typed version
type Entries<T> = {
	[K in keyof T]: [K, T[K]];
}[keyof T][];
export const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

export function IsiOs() {
	return !!navigator.userAgent.match(/iPhone|iPad|iPod/i);
}

export function isStandalone() {
	// @ts-expect-error not sure how to type this
	return (window.navigator as unknown).standalone || window.matchMedia('(display-mode: standalone)').matches;
}

// Is not computed and does not know about Vue Router, just checks the params on load
export const isEmbedded = !!new URLSearchParams(window.location.search).get('rsEmbed');

export function youtubeIdFromUrl(url: string) {
	const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
	const match = url.match(regExp);
	if (match && match[2].length === 11) {
		return match[2];
	} else {
		return '';
	}
}

export const capitalise = <T extends string>(s: T) => {
	if (!s) {
		return '';
	}
	return (s[0].toUpperCase() + s.slice(1)) as Capitalize<typeof s>;
};

export const wrapBackgroundImageUrlToCss = (url: string) => {
	return `url("${url}")`;
};

export const getOtherResourceStatusKey = (resourceStatusKey: ResourceStatusKey): ResourceStatusKey => {
	return resourceStatusKey === resourceStatusKeys.Completed ? resourceStatusKeys.Todo : resourceStatusKeys.Completed;
};

export const getHeroUrl = (resource: ResourceBase): string => {
	if (resource.youtube_id) {
		return youtubeImgSrc(resource);
	}

	return resource.hero.src;
};

// combine this with ResourceSingleVideoComponent.vue
const youtubeImgSrc = (resource: ResourceBase): string => {
	// youtube_id is stored as the bare 11-char video ID (normalised on save in the backend).
	const id = resource.youtube_id;
	// https://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
	return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
};

export const toTitleCase = function (str: string) {
	if (!str) {
		return '';
	}

	return str.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
};

export const pluralise = (word: string, count: number): string => {
	return count === 1 ? word : `${word}s`;
};

/**
 * Don't use this for user input as can lead to XSS attacks
 */
export const htmlDecode = (input: string): string => {
	const e = document.createElement('textarea');
	e.innerHTML = input;
	// handle case of empty input
	return e.childNodes.length === 0 ? '' : (e.childNodes[0].nodeValue ?? '');
};

export function timeout(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
